import React, { useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useCompany } from '../../Contexts/CompanyContext';
import {
  Country,
  StateProvince,
} from '../../Services/shippingService';
import { useShippingCountries } from '../../Hooks/useShippingQuery';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Returns true if every state in a country shares the same shipping cost. */
const allStatesUniform = (country: Country): boolean => {
  if (!country.states || country.states.length === 0) return true;
  const first = country.states[0].shippingCost;
  return country.states.every((s) => s.shippingCost === first);
};

/** Lowest shipping cost across all states (or defaultShippingCost). */
const minShippingCost = (country: Country): number => {
  if (!country.states || country.states.length === 0)
    return country.defaultShippingCost;
  return Math.min(...country.states.map((s) => s.shippingCost));
};

/** Highest shipping cost across all states (or defaultShippingCost). */
const maxShippingCost = (country: Country): number => {
  if (!country.states || country.states.length === 0)
    return country.defaultShippingCost;
  return Math.max(...country.states.map((s) => s.shippingCost));
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Sub-components                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

interface FreeShippingBadgeProps {
  limit: number;
  formatPrice: (v: number) => string;
}
const FreeShippingBadge: React.FC<FreeShippingBadgeProps> = ({ limit, formatPrice }) => {
  if (!limit || limit <= 0) return null;
  return (
    <span className="sp-free-badge">
      <i className="zmdi zmdi-check" style={{ marginRight: 4 }}></i>
      Free over {formatPrice(limit)}
    </span>
  );
};

interface StatePillProps {
  state: StateProvince;
  countryFreeLimit: number;
  formatPrice: (v: number) => string;
}
const StatePill: React.FC<StatePillProps> = ({ state, countryFreeLimit, formatPrice }) => {
  const effectiveLimit =
    state.freeShippingLimit !== undefined && state.freeShippingLimit !== null
      ? state.freeShippingLimit
      : countryFreeLimit;

  return (
    <div className="sp-state-pill">
      <span className="sp-state-name">{state.name}</span>
      <span className="sp-state-cost">
        {state.shippingCost === 0 ? (
          <span style={{ color: '#28a745', fontWeight: 700 }}>Free</span>
        ) : (
          formatPrice(state.shippingCost)
        )}
      </span>
      {effectiveLimit > 0 && (
        <span className="sp-state-free-hint">
          Free over {formatPrice(effectiveLimit)}
        </span>
      )}
    </div>
  );
};

interface CountryCardProps {
  country: Country;
  formatPrice: (v: number) => string;
  isExpanded: boolean;
  onToggle: () => void;
}
const CountryCard: React.FC<CountryCardProps> = ({
  country,
  formatPrice,
  isExpanded,
  onToggle,
}) => {
  const hasStates = country.states && country.states.length > 0;
  const uniform = allStatesUniform(country);
  const minCost = minShippingCost(country);
  const maxCost = maxShippingCost(country);

  const shippingLabel = () => {
    if (!hasStates || uniform) {
      const cost = hasStates ? country.states[0].shippingCost : country.defaultShippingCost;
      return cost === 0 ? (
        <span className="sp-cost sp-cost--free">Free</span>
      ) : (
        <span className="sp-cost">{formatPrice(cost)}</span>
      );
    }
    return (
      <span className="sp-cost sp-cost--range">
        {formatPrice(minCost)}
        {minCost !== maxCost && <> – {formatPrice(maxCost)}</>}
      </span>
    );
  };

  return (
    <div className={`sp-card ${isExpanded ? 'sp-card--open' : ''}`}>
      {/* Card header */}
      <div
        className="sp-card__header"
        onClick={hasStates ? onToggle : undefined}
        style={{ cursor: hasStates ? 'pointer' : 'default' }}
      >
        <div className="sp-card__left">
          <span className="sp-country-code">{country.code}</span>
          <span className="sp-country-name">{country.name}</span>
        </div>

        <div className="sp-card__right">
          {shippingLabel()}
          <FreeShippingBadge
            limit={country.freeShippingLimit}
            formatPrice={formatPrice}
          />
          {hasStates && (
            <i
              className={`zmdi zmdi-chevron-down sp-chevron ${isExpanded ? 'sp-chevron--up' : ''}`}
            ></i>
          )}
        </div>
      </div>

      {/* Expandable states */}
      {hasStates && isExpanded && (
        <div className="sp-card__states">
          <div className="sp-states-grid">
            {country.states.map((state) => (
              <StatePill
                key={state.id}
                state={state}
                countryFreeLimit={country.freeShippingLimit}
                formatPrice={formatPrice}
              />
            ))}
          </div>

          {country.defaultShippingCost > 0 && (
            <p className="sp-default-note">
              <i className="zmdi zmdi-info-outline m-r-5"></i>
              Default rate for unlisted areas: {formatPrice(country.defaultShippingCost)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const ShippingRates: React.FC = () => {
  const { formatPrice } = useCompany();

  // React Query replaces the manual useEffect + local countries/loading state.
  // The result is cached for 30 minutes (matching the service-layer cache) so
  // navigating away and back never triggers a second Firestore read within that
  // window. The service's own localStorage cache still works as a cold-start
  // fallback before React Query has made its first fetch this session.
  const {
    data: countries = [],
    isLoading: loading,
  } = useShippingCountries();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () =>
    setExpandedIds(new Set(countries.filter((c) => c.states?.length > 0).map((c) => c.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const filtered = countries.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.states?.some((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const hasFreeShipping = countries.some((c) => c.freeShippingLimit > 0);
  const minFreeThreshold = countries
    .filter((c) => c.freeShippingLimit > 0)
    .reduce((min, c) => Math.min(min, c.freeShippingLimit), Infinity);

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Shipping Rates</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Page header ── */}
          <div className="sp-hero">
            <div className="sp-hero__icon">
              <i className="zmdi zmdi-truck"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Shipping Rates</h2>
            <p className="stext-113 cl6 sp-hero__sub">
              Transparent pricing for every destination. Select your country to see regional rates.
            </p>

            {/* Free shipping highlight */}
            {hasFreeShipping && isFinite(minFreeThreshold) && (
              <div className="sp-hero__banner">
                <i className="zmdi zmdi-star m-r-8"></i>
                Free shipping available on qualifying orders — starting from{' '}
                <strong>{formatPrice(minFreeThreshold)}</strong>
              </div>
            )}
          </div>

          {/* ── Stats strip ── */}
          {!loading && countries.length > 0 && (
            <div className="sp-stats">
              <div className="sp-stat">
                <span className="sp-stat__num">{countries.length}</span>
                <span className="sp-stat__label">Countries</span>
              </div>
              <div className="sp-stat-divider" />
              <div className="sp-stat">
                <span className="sp-stat__num">
                  {countries.reduce((t, c) => t + (c.states?.length || 0), 0)}
                </span>
                <span className="sp-stat__label">Regions</span>
              </div>
              <div className="sp-stat-divider" />
              <div className="sp-stat">
                <span className="sp-stat__num">
                  {countries.filter((c) => c.freeShippingLimit > 0).length}
                </span>
                <span className="sp-stat__label">Free Shipping Destinations</span>
              </div>
            </div>
          )}

          {/* ── Search + controls ── */}
          {!loading && countries.length > 0 && (
            <div className="sp-toolbar">
              <div className="sp-search bor8 bg0">
                <i className="zmdi zmdi-search sp-search__icon"></i>
                <input
                  className="stext-111 cl8"
                  type="text"
                  placeholder="Search country or region..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="sp-search__clear"
                    onClick={() => setSearchQuery('')}
                  >
                    <i className="zmdi zmdi-close"></i>
                  </button>
                )}
              </div>

              <div className="sp-toolbar__actions">
                <button className="sp-btn-ghost" onClick={expandAll}>
                  Expand All
                </button>
                <button className="sp-btn-ghost" onClick={collapseAll}>
                  Collapse All
                </button>
              </div>
            </div>
          )}

          {/* ── Content ── */}
          {loading ? (
            <div className="sp-loading">
              <div className="sp-spinner"></div>
              <p className="stext-113 cl6 p-t-20">Loading shipping rates...</p>
            </div>
          ) : countries.length === 0 ? (
            <div className="sp-empty">
              <i className="zmdi zmdi-info-outline" style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }}></i>
              <p className="stext-113 cl6">No shipping rates configured yet.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="sp-empty">
              <i className="zmdi zmdi-search" style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }}></i>
              <p className="stext-113 cl6">No results for "<strong>{searchQuery}</strong>"</p>
              <button className="sp-btn-ghost m-t-15" onClick={() => setSearchQuery('')}>
                Clear search
              </button>
            </div>
          ) : (
            <div className="sp-list">
              {filtered.map((country, i) => (
                <div
                  key={country.id}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className="sp-list__item"
                >
                  <CountryCard
                    country={country}
                    formatPrice={formatPrice}
                    isExpanded={expandedIds.has(country.id)}
                    onToggle={() => toggleExpanded(country.id)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── Info footer ── */}
          {!loading && countries.length > 0 && (
            <div className="sp-info-row">
              <div className="sp-info-card">
                <i className="zmdi zmdi-time sp-info-card__icon"></i>
                <div>
                  <p className="sp-info-card__title">Processing Time</p>
                  <p className="sp-info-card__body">Orders are processed within 1–2 business days after payment confirmation.</p>
                </div>
              </div>
              <div className="sp-info-card">
                <i className="zmdi zmdi-alert-circle-o sp-info-card__icon"></i>
                <div>
                  <p className="sp-info-card__title">Rate Changes</p>
                  <p className="sp-info-card__body">Shipping rates are subject to change. The rate at checkout is final.</p>
                </div>
              </div>
              <div className="sp-info-card">
                <i className="zmdi zmdi-email sp-info-card__icon"></i>
                <div>
                  <p className="sp-info-card__title">Questions?</p>
                  <p className="sp-info-card__body">
                    Contact our support team if you don't see your region listed.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .sp-hero {
          text-align: center;
          padding-bottom: 40px;
        }
        .sp-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 32px;
          color: #717fe0;
        }
        .sp-hero__sub {
          max-width: 520px;
          margin: 0 auto 20px;
          line-height: 1.7;
        }
        .sp-hero__banner {
          display: inline-block;
          background: #f0f4ff;
          border: 1px solid #d0d8f8;
          color: #4a55c8;
          border-radius: 6px;
          padding: 10px 22px;
          font-size: 13px;
          margin-top: 8px;
        }

        /* ── Stats ── */
        .sp-stats {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          background: #fafafa;
          border: 1px solid #e6e6e6;
          border-radius: 8px;
          padding: 20px 40px;
          margin-bottom: 32px;
        }
        .sp-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 32px;
        }
        .sp-stat__num {
          font-size: 28px;
          font-weight: 700;
          color: #333;
          line-height: 1;
        }
        .sp-stat__label {
          font-size: 12px;
          color: #888;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }
        .sp-stat-divider {
          width: 1px;
          height: 40px;
          background: #e0e0e0;
        }

        /* ── Toolbar ── */
        .sp-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .sp-search {
          flex: 1;
          min-width: 220px;
          display: flex;
          align-items: center;
          border: 1px solid #e6e6e6;
          border-radius: 4px;
          padding: 0 14px;
          background: #fff;
        }
        .sp-search__icon {
          color: #aaa;
          margin-right: 10px;
          font-size: 16px;
          flex-shrink: 0;
        }
        .sp-search input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          padding: 11px 0;
          font-size: 13px;
          color: #555;
          font-family: Poppins-Regular, sans-serif;
        }
        .sp-search__clear {
          background: none;
          border: none;
          cursor: pointer;
          color: #aaa;
          padding: 4px;
          font-size: 16px;
          line-height: 1;
        }
        .sp-search__clear:hover { color: #555; }
        .sp-toolbar__actions {
          display: flex;
          gap: 8px;
        }
        .sp-btn-ghost {
          background: none;
          border: 1px solid #d0d0d0;
          border-radius: 4px;
          padding: 8px 16px;
          font-size: 12px;
          color: #666;
          cursor: pointer;
          font-family: Poppins-Regular, sans-serif;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .sp-btn-ghost:hover {
          border-color: #717fe0;
          color: #717fe0;
          background: #f4f4ff;
        }

        /* ── Country card ── */
        .sp-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 50px;
        }
        .sp-list__item {
          animation: spFadeIn 0.35s ease both;
        }
        @keyframes spFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sp-card {
          border: 1px solid #e6e6e6;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .sp-card:hover {
          border-color: #c8cef5;
          box-shadow: 0 2px 12px rgba(113,127,224,0.07);
        }
        .sp-card--open {
          border-color: #717fe0;
        }
        .sp-card__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 22px;
          gap: 12px;
          flex-wrap: wrap;
          user-select: none;
        }
        .sp-card__left {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .sp-country-code {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 26px;
          background: #f0f4ff;
          color: #717fe0;
          font-size: 10px;
          font-weight: 700;
          border-radius: 3px;
          letter-spacing: 1px;
          flex-shrink: 0;
        }
        .sp-country-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }
        .sp-card__right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .sp-cost {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
        }
        .sp-cost--free {
          color: #28a745;
        }
        .sp-cost--range {
          color: #555;
        }
        .sp-free-badge {
          display: inline-flex;
          align-items: center;
          background: #e8f5e9;
          color: #2e7d32;
          font-size: 11px;
          font-weight: 600;
          border-radius: 3px;
          padding: 3px 10px;
          white-space: nowrap;
        }
        .sp-chevron {
          font-size: 18px;
          color: #aaa;
          transition: transform 0.25s ease;
          margin-left: 4px;
        }
        .sp-chevron--up {
          transform: rotate(180deg);
          color: #717fe0;
        }

        /* ── States panel ── */
        .sp-card__states {
          border-top: 1px solid #eee;
          background: #fafbff;
          padding: 18px 22px 14px;
          animation: spSlideDown 0.2s ease;
        }
        @keyframes spSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sp-states-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 8px;
        }
        .sp-state-pill {
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 5px;
          padding: 10px 14px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          transition: border-color 0.2s;
        }
        .sp-state-pill:hover {
          border-color: #c8cef5;
        }
        .sp-state-name {
          font-size: 12px;
          font-weight: 500;
          color: #444;
        }
        .sp-state-cost {
          font-size: 13px;
          font-weight: 700;
          color: #333;
        }
        .sp-state-free-hint {
          font-size: 10px;
          color: #28a745;
          margin-top: 2px;
        }
        .sp-default-note {
          font-size: 11px;
          color: #888;
          margin-top: 12px;
          display: flex;
          align-items: center;
        }

        /* ── Loading / empty ── */
        .sp-loading, .sp-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }
        .sp-spinner {
          width: 36px;
          height: 36px;
          border: 3px solid #eee;
          border-top-color: #717fe0;
          border-radius: 50%;
          animation: sp-spin 0.7s linear infinite;
        }
        @keyframes sp-spin {
          to { transform: rotate(360deg); }
        }

        /* ── Info row ── */
        .sp-info-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-top: 10px;
        }
        .sp-info-card {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          background: #fafafa;
          border: 1px solid #ebebeb;
          border-radius: 6px;
          padding: 18px 20px;
        }
        .sp-info-card__icon {
          font-size: 22px;
          color: #717fe0;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .sp-info-card__title {
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
        }
        .sp-info-card__body {
          font-size: 12px;
          color: #888;
          line-height: 1.6;
        }

        /* ── Responsive ── */
        @media (max-width: 576px) {
          .sp-stats {
            padding: 16px 10px;
          }
          .sp-stat {
            padding: 0 16px;
          }
          .sp-stat__num {
            font-size: 22px;
          }
          .sp-toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          .sp-toolbar__actions {
            justify-content: flex-end;
          }
          .sp-card__header {
            padding: 14px 16px;
          }
          .sp-states-grid {
            grid-template-columns: 1fr 1fr;
          }
          .sp-info-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ShippingRates;